import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Component, useEffect } from 'react';
import type { ErrorInfo, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { isAdmin, useIdentity } from '@/entities/session';
import { AdminScreen, ConnectorsScreen } from '@/features/admin';
import {
  ChangePasswordForm,
  ForgotPasswordForm,
  LoginForm,
  ResetPasswordForm,
  SignupForm,
  ActivateAccountPanel,
  VerifyEmailPanel,
} from '@/features/auth';

import { AuthShell } from './AuthShell';
import { useSessionRestoreGuard } from '@/shared/auth/restoreGuard';
import { isAuthenticated, mustChangePassword } from '@/shared/auth/session';
import { useEmbedded } from '@/shared/embedded';
import {
  DatasetDetail,
  SourceCatalogue,
  SourceDatasets,
} from '@/features/catalog-browser';
import { ClassificationReview } from '@/features/classification-review';
import { ConnectionDetail, Connections } from '@/features/connections';
import { IntegrationsScreen } from '@/features/itsm';
import { LineageScreen } from '@/features/lineage';
import { ProfileDetail, ProfilesScreen } from '@/features/profiles';
import { AlertRoutingScreen } from '@/features/alert-routing';
import { HomeScreen } from '@/features/home';
import { ModelRoutingScreen } from '@/features/model-routing';
import { IncidentDetail, IncidentQueue } from '@/features/incidents';
import { CompliancePacks, FrameworkControls } from '@/features/compliance';
import { ContractDetail, ContractsLedger } from '@/features/contracts';
import { UsageAndCost } from '@/features/metering';
import { MarketplaceScreen } from '@/features/marketplace';
import { AnomaliesScreen, AnomalyDetail } from '@/features/anomaly';
import { CapabilityMatrixScreen } from '@/features/evals';
import { AutonomousLog } from '@/features/autonomous';
import { CopilotConsole } from '@/features/copilot';
import { SuggestionsScreen } from '@/features/rulegen';
import { DefinitionRuns, ReconciliationBoard } from '@/features/reconciliation';
import { PlanDetail, RemediationQueue } from '@/features/remediation';
import { AuditTrailScreen } from '@/features/audit';
import { BulkRunsScreen } from '@/features/bulk-ops';
import { PolicyBundlesScreen } from '@/features/policy';
import { PlatformStatus } from '@/features/platform-status';
import { ExecutionDetail, ExecutionsScreen, RulesScreen } from '@/features/rules';
import { ScoreModelScreen, ScoresScreen } from '@/features/scoring';
import { Alert } from '@/shared/ui/Alert';
import { EmptyState } from '@/shared/ui/EmptyState';
import { LoadingState } from '@/shared/ui/States';

import { AppShell } from './AppShell';
import { LANDING_PATH, SCREEN_PATHS } from '@/shared/routes';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /* No automatic retry. A failing control-plane call should be visible immediately rather than
       * hidden behind three silent attempts — the operator watching this screen needs the signal,
       * not a smoothed-over one. */
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

interface ErrorBoundaryState {
  message: string | null;
}

/* §20 puts error boundaries in `app/`. Without one, a render error in any feature blanks the entire
 * page and the browser console is the only clue. */
class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { message: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { message: error.message };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /* Console only, deliberately. Shipping errors to a SaaS collector would violate hard rule 13;
     * a real sink is the customer's own, wired through the observability stack. */
    console.error('unhandled render error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.message !== null) {
      return (
        <div
          style={{
            maxWidth: '640px',
            margin: 'var(--space-7) auto',
            padding: '0 var(--space-5)',
          }}
        >
          {/* **The one place a literal string is correct**, and the reason is the boundary itself:
              `t()` reads from the i18n instance, which initialises on import and is a plausible
              cause of the very crash being reported. A boundary that depends on the system it is
              reporting on cannot report on that system failing. */}
          <Alert tone="error" title="This screen failed to render">
            {this.state.message}
          </Alert>
        </div>
      );
    }
    return this.props.children;
  }
}

/* Whether this build gates the app behind ADR-012's local auth.
 *
 * **Opt-in, and that is the point.** Most deployments run OIDC: a validated claim arrives from
 * `api-gateway` or the dev proxy with no login screen involved at all, and `isAuthenticated()` below
 * — which only ever becomes true after a call to `setToken` from `LoginForm`/`SignupForm` — would
 * never fire for them. Gating the whole app on it unconditionally would lock out every existing
 * deployment that has never heard of `/login`. So this reads a build-time flag, mirroring how the
 * backend itself only mounts `/v1/auth` when `LUMIQ_LOCAL_AUTH_ENABLED` is set — the same decision,
 * made in the same place a deployment already makes it.
 */
const LOCAL_AUTH_BUILD = import.meta.env['VITE_LUMIQ_LOCAL_AUTH'] === '1';

/* Somebody whose password was chosen for them goes to one screen and nowhere else.
 *
 * **This is what makes the server's refusal usable.** `OidcTenantResolver` refuses every
 * authenticated route while the token carries the obligation, and without this guard the reader
 * landed wherever they were heading and met that refusal as a failed panel — on the Rules screen it
 * read "Could not load rules" above the server's own instruction to POST to an endpoint. The
 * enforcement was real and the experience was a broken page.
 *
 * Both directions, and the second is not symmetry for its own sake. With the obligation, every path
 * leads here. *Without* it, this path leads away — because a form asking for a password nobody needs
 * to change is a form that invites a password to be changed by accident, and it would refuse on
 * submit anyway since the server has nothing to discharge.
 */
function RequirePasswordChange({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  /* One direction only now. The inverse — sending somebody *away* from the form once there is
     nothing to change — moved to `ChangePasswordScreen` when the form left this subtree, because a
     guard can only redirect away from a route it renders. Keeping a `pathname` comparison here
     would be dead code that reads like a live rule. */
  if (mustChangePassword()) {
    return <Navigate to={SCREEN_PATHS.changePassword} replace />;
  }
  return <>{children}</>;
}

/* Inside the Suite shell with no token: the shell's own session has lapsed or never reached us.
 *
 * **Reported to the shell, never redirected.** The shell owns sign-in, shows its re-auth dialog and
 * remounts Lumiq at the same path afterwards; `/login` does not exist in the embedded route table at
 * all. Reported from an effect rather than during render, because a render may run more than once
 * (StrictMode, a concurrent retry) and the shell should hear it once per mount. */
function AwaitShellSession({
  onSessionExpired,
}: {
  readonly onSessionExpired: () => void;
}): null {
  useEffect(() => {
    onSessionExpired();
  }, [onSessionExpired]);
  return null;
}

function RequireAuth({ children }: { readonly children: ReactNode }): ReactElement {
  /* Read unconditionally: a hook cannot sit behind the early return below. */
  const location = useLocation();
  const embedded = useEmbedded();
  if (embedded !== null) {
    return isAuthenticated() ? (
      <>{children}</>
    ) : (
      <AwaitShellSession onSessionExpired={embedded.onSessionExpired} />
    );
  }
  if (!LOCAL_AUTH_BUILD || isAuthenticated()) return <>{children}</>;
  /* `<Navigate>`, not `useNavigate()` called during render — redirecting from inside a render is
   * exactly what `<Navigate>` exists for, and calling the imperative form here would be a
   * setState-during-render warning on every unauthenticated visit.
   *
   * **`state` carries where they were going, and without it a deep link is a dead link.** An alert
   * mails `/incidents/<id>`; an unauthenticated reader clicking it was sent to `/login` with the
   * destination discarded, signed in, and landed on the dashboard — so the link the mail exists to
   * provide delivered them somewhere they then had to navigate away from.
   *
   * **Router state rather than a `?next=` query parameter, and that is a security choice.** A query
   * parameter is attacker-controllable: anyone could mail `/login?next=https://evil.example` and
   * have this app bounce a freshly-authenticated reader to it. `state` is set by the app during a
   * client-side navigation and cannot be forged from a URL, so no allow-list and no relative-path
   * check is needed. If this ever becomes a query parameter, that validation becomes mandatory. */
  return <Navigate to={SCREEN_PATHS.login} state={{ from: location }} replace />;
}

/* The Admin surface, refused rather than merely unlinked.
 *
 * **An unlinked route is still reachable by typing the URL**, and that is not an academic point here:
 * `AdminScreen` reads the tenant's user listing, its roles and the permission catalogue, so a
 * non-admin who guessed `/admin` got a page of three failed reads. Every one of those is a correct
 * 403 and none of them says so — the screen renders "we could not read this" three times, which is
 * the sentence reserved for a genuine fault. So the refusal happens before the reads.
 *
 * **A refusal in place rather than a redirect**, for the reason every absence in this product is
 * named rather than hidden: bouncing somebody to the dashboard tells them nothing, and the reader who
 * followed a colleague's link needs to know that the door exists and their roles do not open it.
 *
 * **Pending is its own branch and is not a refusal.** `can` denies while the identity is in flight —
 * correctly, because optimistically showing a control that then vanishes reads as a bug — so
 * rendering the refusal during the load would flash "not permitted" at an administrator on every
 * hard navigation to their own screen.
 *
 * This hides a screen. It protects nothing: every route under it refuses server-side regardless of
 * what this browser believes, which is the property `kernel.authz` exists to hold.
 */
function RequireAdmin({ children }: { readonly children: ReactNode }): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();

  if (identity.isPending)
    return <LoadingState rows={3} label={t('admin.gate.reading')} />;
  if (isAdmin(identity.data)) return <>{children}</>;
  return (
    <EmptyState title={t('admin.gate.refusedTitle')} guidance={t('admin.gate.refused')} />
  );
}

/* Placeholders for the screens the next tasks build, and they are deliberately honest rather than
 * "coming soon". A page that says what is missing and why is a page a reviewer can act on; one that
 * says nothing invites the assumption that the feature exists and is broken. */
function PendingScreen({
  titleKey,
  guidanceKey,
}: {
  readonly titleKey: string;
  readonly guidanceKey: string;
}): ReactElement {
  const { t } = useTranslation();
  return <EmptyState title={t(titleKey)} guidance={t(guidanceKey)} />;
}

/* Thin wrappers around `LoginForm`/`SignupForm` that own the one thing the form itself should not
 * decide: where to go once the token exists. `useNavigate` belongs here, in a component React
 * Router actually renders as a route element, rather than inside the form. */
/** Where `RequireAuth` recorded the reader was going, or `null`.
 *
 * Narrowed here rather than trusted, because `history.state` is not typed and survives a reload
 * only as whatever the browser kept. A shape that is not `{ from: { pathname } }` is treated as
 * absent, so the fallback is the dashboard rather than a crash on a malformed entry.
 *
 * **Only `pathname`, `search` and `hash` are carried through.** Taking the whole location object
 * would hand `navigate` an `origin` from somewhere, which is the open redirect that using router
 * state instead of a query parameter exists to avoid — a relative target cannot leave this app.
 */
export function intendedDestination(state: unknown): string | null {
  if (typeof state !== 'object' || state === null || !('from' in state)) return null;
  const { from } = state;
  if (typeof from !== 'object' || from === null || !('pathname' in from)) return null;
  const { pathname, search, hash } = from as {
    pathname: unknown;
    search?: unknown;
    hash?: unknown;
  };
  /* **`//` is refused as well as a missing leading slash**, and that second clause is the one that
     is easy to miss: `//evil.example/x` starts with `/` and is a *protocol-relative URL*, which a
     browser resolves against the current scheme and sends off-origin. A single leading slash alone
     is not a same-origin check. */
  if (
    typeof pathname !== 'string' ||
    !pathname.startsWith('/') ||
    pathname.startsWith('//')
  ) {
    return null;
  }
  return `${pathname}${typeof search === 'string' ? search : ''}${typeof hash === 'string' ? hash : ''}`;
}

function LoginScreen(): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  /* **The destination survives the sign-in, or the dashboard does.** A reader arriving from an
     alert's `/incidents/<id>` link lands on the incident rather than on a dashboard they then have
     to navigate away from.

     It does not survive a page reload: router state belongs to a history entry, and reloading
     `/login` discards it. Clicking the link works, which is the case the alert creates; carrying it
     further would mean `sessionStorage` and a lifetime to manage for a much rarer path. */
  const destination = intendedDestination(location.state);
  return (
    <AuthShell>
      <LoginForm
        onLoggedIn={() => {
          navigate(destination ?? LANDING_PATH, { replace: true });
        }}
      />
    </AuthShell>
  );
}

function ChangePasswordScreen(): ReactElement {
  const location = useLocation();
  /* **Two guards, because this route is no longer inside the subtree `RequirePasswordChange`
     protects.** That component did both directions at once and could only do so because the form
     was one of its children; moving the form out took the "away" half with it, and without this the
     screen would stay reachable for ever after the password was changed — a form inviting somebody
     to change a password nobody needs to change, which would then refuse on submit because the
     server has nothing left to discharge.

     `RequireAuth` first: the request needs a token, and an unauthenticated visitor belongs at
     `/login` rather than at a form that cannot possibly succeed. */
  if (!LOCAL_AUTH_BUILD || !isAuthenticated()) {
    /* Carries the destination for `RequireAuth`'s reason: somebody following a deep link whose
       session has lapsed reaches this guard too, and discarding it here would lose the link on
       exactly the path that already asks the most of them. */
    return <Navigate to={SCREEN_PATHS.login} state={{ from: location }} replace />;
  }
  if (!mustChangePassword()) {
    return <Navigate to={SCREEN_PATHS.home} replace />;
  }
  return (
    <AuthShell>
      <ChangePasswordForm />
    </AuthShell>
  );
}

function SignupScreen(): ReactElement {
  const navigate = useNavigate();
  return (
    <AuthShell>
      <SignupForm
        onSignedUp={() => {
          // **`/login`, not `LANDING_PATH`.** ADR-014: signup no longer returns a session, so the
          // dashboard would bounce off `RequireAuth` and strand the reader on a login screen with
          // no idea why. `replace` so Back does not return to a form whose account already exists.
          navigate(SCREEN_PATHS.login, { replace: true });
        }}
      />
    </AuthShell>
  );
}

/* **Redeeming a verification link, and it lands on `/login` exactly as a completed reset does.**
 *
 * `useConfirmEmailVerification` deliberately stores no token: the reader signs in with the password
 * they chose at signup, which is the action that proves the address works. A mail link that
 * authenticated would make an inbox an authentication path — wider than confirming an address, and
 * the same argument `ResetPasswordScreen` makes for not signing anybody in.
 *
 * The confirmation travels in the router's own state rather than the query string, so a bookmark of
 * `/login` cannot announce a verification that never happened. */
function VerifyEmailScreen(): ReactElement {
  const navigate = useNavigate();
  return (
    <AuthShell>
      <VerifyEmailPanel
        onVerified={() => {
          navigate(SCREEN_PATHS.login, {
            replace: true,
            state: { emailVerified: true },
          });
        }}
      />
    </AuthShell>
  );
}

function ActivateAccountScreen(): ReactElement {
  const navigate = useNavigate();
  return (
    <AuthShell>
      <ActivateAccountPanel
        onActivated={() => {
          /* **Straight to the dashboard, which is the brief's preferred behaviour.**
             `useAcceptInvitation` has already stored the session, so this is a navigation rather
             than a sign-in — and `replace` because the activation URL carries a token that is now
             spent: leaving it in history would let a Back button re-post a dead one and show the
             person a refusal for an account that works. */
          navigate(SCREEN_PATHS.home, { replace: true });
        }}
      />
    </AuthShell>
  );
}

function ForgotPasswordScreen(): ReactElement {
  return (
    <AuthShell>
      <ForgotPasswordForm />
    </AuthShell>
  );
}

/* **A completed reset lands on `/login`, not on the dashboard**, and it does not carry a session.
 * `useResetPassword` deliberately stores no token: the reader signs in with the password they just
 * set, which is the one action that proves it took effect — and a reset that silently signed somebody
 * in would mean a mail link is an authentication path, which is a wider thing than a password change.
 *
 * The confirmation travels in the router's own state rather than in the query string, so a bookmark
 * of `/login` cannot announce a password change that never happened. `LoginForm` reads it through
 * `announcesPasswordChange`, which guards rather than casts. */
function ResetPasswordScreen(): ReactElement {
  const navigate = useNavigate();
  return (
    <AuthShell>
      <ResetPasswordForm
        onDone={() => {
          navigate(SCREEN_PATHS.login, {
            replace: true,
            state: { passwordChanged: true },
          });
        }}
      />
    </AuthShell>
  );
}

export interface AppProps {
  /** True only when the Suite shell mounted Lumiq through `remote/mount.tsx`. */
  readonly embedded?: boolean;
  /** The remote's per-mount cache, so nothing survives an unmount. Standalone uses the module's. */
  readonly queryClient?: QueryClient;
}

export function App({
  embedded = false,
  queryClient: client = queryClient,
}: AppProps = {}): ReactElement {
  /* **Mounted here and nowhere else.** A bfcache restore raises `pageshow` against the document, so
     a listener attached per route would only be installed for whichever route happened to be
     mounted when the page was cached. See the hook for why the route guard cannot cover this. */
  useSessionRestoreGuard();

  return (
    <ErrorBoundary>
      <QueryClientProvider client={client}>
        <Routes>
          {/* **None of the public routes exist inside the Suite shell.** The shell owns sign-in,
              and every one of these either is a sign-in or ends on `/login`, which must never
              appear inside the suite. Mail links (reset, verify, activate) keep pointing at
              standalone Lumiq, which is where these screens still live. */}
          {embedded ? null : (
            <>
              {/* Public, outside `AppShell` and outside `RequireAuth` — the two routes that exist
                  precisely because no principal exists yet (ADR-012). */}
              <Route path={SCREEN_PATHS.login} element={<LoginScreen />} />
              <Route path={SCREEN_PATHS.signup} element={<SignupScreen />} />
              {/* Public for the same reason and deliberately *not* guarded against a signed-in
                  reader: a reset link is single-use and carries the only thing that can spend it,
                  so redirecting a reader who happens to hold a token in this tab would strand a
                  valid reset with nothing able to complete it. `/login` and `/signup` are unguarded
                  on the same grounds — one rule for the four public routes rather than two. */}
              <Route
                path={SCREEN_PATHS.forgotPassword}
                element={<ForgotPasswordScreen />}
              />
              <Route
                path={SCREEN_PATHS.resetPassword}
                element={<ResetPasswordScreen />}
              />
              {/* Public for the reset route's reason, and deliberately not guarded against a
                  signed-in reader: a verification link is single-use and carries the only thing
                  that can spend it, so redirecting somebody who happens to hold a session in this
                  tab would strand a valid link with no way to redeem it. */}
              <Route path={SCREEN_PATHS.verifyEmail} element={<VerifyEmailScreen />} />
              <Route path={SCREEN_PATHS.activate} element={<ActivateAccountScreen />} />
              {/* **Authenticated, and deliberately outside `AppShell`.** It used to be a child of
                  the shell, on the argument that the reader "keeps their bearings" — which was
                  wrong, and obviously so once seen: the page renders a sidebar of twenty links, a
                  dataset search box and a notification bell around a form whose own copy says this
                  is the only screen available. Every one of those controls leads somewhere the
                  server refuses.

                  `AuthShell` is the right frame and it already exists — the centred, chromeless
                  one `/login` and `/signup` use. Reusing it rather than styling a third layout is
                  also what keeps the two screens looking like one product: somebody who signed in
                  ten seconds ago should recognise where they are. */}
              <Route
                path={SCREEN_PATHS.changePassword}
                element={<ChangePasswordScreen />}
              />
            </>
          )}
          <Route
            element={
              <RequireAuth>
                {/* Inside `RequireAuth` and outside `AppShell`'s children, so it runs for every
                    authenticated route with one declaration rather than being remembered per
                    route — the shape that makes a forgotten route the default failure. */}
                <RequirePasswordChange>
                  <AppShell />
                </RequirePasswordChange>
              </RequireAuth>
            }
          >
            {/* Datasets is the landing page, not the platform screen. The catalog is what the
                product is for; deployment health belongs in a banner, which task #36 replaced the
                table with. */}
            {/* The index route renders Home rather than redirecting to it. `LANDING_PATH` is now
                `/` itself, so a redirect here would be a loop — and the previous redirect existed
                only because there was no page at the root worth showing. */}
            <Route index element={<HomeScreen />} />
            <Route path={SCREEN_PATHS.connections} element={<Connections />} />
            <Route path={SCREEN_PATHS.connection} element={<ConnectionDetail />} />
            <Route path={SCREEN_PATHS.datasets} element={<SourceCatalogue />} />
            {/* **Declared before `:datasetId`.** React Router v6 ranks routes by specificity rather
                than by declaration order, so a static segment already beats a dynamic one and this
                pair would resolve correctly either way — the order is here because a reader
                scanning the file should see the more specific route first, and because the
                equivalent pair on the server *is* order-dependent (§9 records `GET /v1/rules/bulk`
                shipping broken for exactly that reason). Two conventions for one URL shape is how
                one of them gets forgotten. */}
            <Route path={SCREEN_PATHS.datasetsBySource} element={<SourceDatasets />} />
            <Route path={SCREEN_PATHS.dataset} element={<DatasetDetail />} />
            <Route path={SCREEN_PATHS.profiles} element={<ProfilesScreen />} />
            <Route path={SCREEN_PATHS.profile} element={<ProfileDetail />} />
            <Route path={SCREEN_PATHS.review} element={<ClassificationReview />} />
            <Route path={SCREEN_PATHS.rules} element={<RulesScreen />} />
            {/* One rule opens as a panel over the list, so its address renders the list too. */}
            <Route path={SCREEN_PATHS.rule} element={<RulesScreen />} />
            <Route path={SCREEN_PATHS.executions} element={<ExecutionsScreen />} />
            <Route path={SCREEN_PATHS.execution} element={<ExecutionDetail />} />
            <Route path={SCREEN_PATHS.scores} element={<ScoresScreen />} />
            <Route path={SCREEN_PATHS.lineage} element={<LineageScreen />} />
            <Route path={SCREEN_PATHS.incidents} element={<IncidentQueue />} />
            <Route path={SCREEN_PATHS.incident} element={<IncidentDetail />} />
            <Route path={SCREEN_PATHS.alertRouting} element={<AlertRoutingScreen />} />
            <Route path={SCREEN_PATHS.serving} element={<ModelRoutingScreen />} />
            <Route path={SCREEN_PATHS.remediation} element={<RemediationQueue />} />
            <Route path={SCREEN_PATHS.remediationPlan} element={<PlanDetail />} />
            <Route path={SCREEN_PATHS.compliance} element={<CompliancePacks />} />
            <Route
              path={SCREEN_PATHS.complianceFramework}
              element={<FrameworkControls />}
            />
            <Route path={SCREEN_PATHS.contracts} element={<ContractsLedger />} />
            <Route path={SCREEN_PATHS.contract} element={<ContractDetail />} />
            <Route path={SCREEN_PATHS.metering} element={<UsageAndCost />} />
            <Route path={SCREEN_PATHS.copilot} element={<CopilotConsole />} />
            <Route path={SCREEN_PATHS.suggestions} element={<SuggestionsScreen />} />
            <Route path={SCREEN_PATHS.anomalies} element={<AnomaliesScreen />} />
            <Route path={SCREEN_PATHS.anomaly} element={<AnomalyDetail />} />
            <Route path={SCREEN_PATHS.evals} element={<CapabilityMatrixScreen />} />
            <Route path={SCREEN_PATHS.autonomous} element={<AutonomousLog />} />
            <Route path={SCREEN_PATHS.reconciliation} element={<ReconciliationBoard />} />
            {/* The two drill-downs. Declared most-specific-first for the reason the dataset pair
                above states: React Router ranks by specificity so the order is for the reader, and
                the equivalent pair on the server *is* order-dependent. */}
            {/* One run's breaks open in a drawer over the definition they belong to, so the run's
                address renders the definition page with the drawer open. */}
            <Route path={SCREEN_PATHS.reconciliationRun} element={<DefinitionRuns />} />
            <Route
              path={SCREEN_PATHS.reconciliationDefinition}
              element={<DefinitionRuns />}
            />
            <Route path={SCREEN_PATHS.marketplace} element={<MarketplaceScreen />} />
            {/* `/platform`, not `/readiness`. The probe at `/readiness` answers a hard navigation
                first — the dev proxy forwards it and a real EdgeGateway serves it — so this screen
                was reachable by clicking and served raw JSON on refresh. See `serverPaths.ts`. */}
            <Route path={SCREEN_PATHS.audit} element={<AuditTrailScreen />} />
            <Route path={SCREEN_PATHS.bulk} element={<BulkRunsScreen />} />
            <Route path={SCREEN_PATHS.policy} element={<PolicyBundlesScreen />} />
            <Route path={SCREEN_PATHS.platform} element={<PlatformStatus />} />
            {/* `/team` is superseded by `/admin` and redirects rather than 404ing: it was the
                add-user form alone, and every link and bookmark to it should land on the surface
                that now contains that form plus the listing it never had. `replace` so Back does
                not bounce the reader between the two. */}
            <Route
              path={SCREEN_PATHS.team}
              element={<Navigate to={SCREEN_PATHS.admin} replace />}
            />
            {/* Every Admin route is gated, not just the landing one: `/admin/connectors` and
                `/admin/score-model` are separate routes, and an unlinked-but-reachable screen is
                exactly what the nav filter cannot fix. See `RequireAdmin`. */}
            <Route
              path={SCREEN_PATHS.admin}
              element={
                <RequireAdmin>
                  <AdminScreen />
                </RequireAdmin>
              }
            />
            <Route
              path={SCREEN_PATHS.connectors}
              element={
                <RequireAdmin>
                  <ConnectorsScreen />
                </RequireAdmin>
              }
            />
            <Route
              path={SCREEN_PATHS.scoreModel}
              element={
                <RequireAdmin>
                  <ScoreModelScreen />
                </RequireAdmin>
              }
            />
            <Route
              path={SCREEN_PATHS.integrations}
              element={
                /* `RequireAdmin` like its siblings, and here it is doing more than matching them:
                   registering a service desk stores a credential, and deleting a connection
                   withdraws every ticket rule pointing at it. Neither belongs behind a steward's
                   grant. */
                <RequireAdmin>
                  <IntegrationsScreen />
                </RequireAdmin>
              }
            />
            <Route
              path="*"
              element={
                <PendingScreen
                  titleKey="notFound.title"
                  guidanceKey="notFound.guidance"
                />
              }
            />
          </Route>
        </Routes>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
